/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Divider, Typography2 } from '@coursera/cds-core';

import ChipButton from 'bundles/search-common/components/experiments/filters2024/ChipButton';
import {
  AI_SUMMARY_CHIP_BUTTON_HEIGHT,
  ON_SITE_SEARCH_PATH,
  SEARCH_SUGGESTIONS_INDEX_ID,
} from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import { isSearchSuggestionHit } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

type Props = {
  onQueryClick?: (query: string) => void;
};

const styles = {
  contentWrapper: css`
    display: flex;
    align-items: flex-start;
    gap: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-300);
    padding-left: var(--cds-spacing-100);
  `,
  suggestedQueriesLabel: css`
    font-weight: 600;
    color: var(--cds-color-grey-975);
    flex-shrink: 0;
    padding-top: var(--cds-spacing-100);
  `,
  chipContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin-left: -3px;
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain; /* Prevent scroll chaining */
    touch-action: pan-x; /* Only allow horizontal panning */

    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  `,
  chipRow: css`
    display: flex;
    gap: var(--cds-spacing-100);
    flex-shrink: 0;
  `,
  dividerWrapper: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-50);
  `,
};

const NLSuggestions = ({ onQueryClick }: Props) => {
  const { getSearchResults } = useSearch();
  const suggestedSearches = getSearchResults(SEARCH_SUGGESTIONS_INDEX_ID);
  const chipContainerRef = useRef<HTMLDivElement>(null);
  const [measuredMaxChipsPerRow, setMeasuredMaxChipsPerRow] = useState<number | null>(null);

  const suggestedSearchHits = React.useMemo(
    () => suggestedSearches?.elements.filter(isSearchSuggestionHit) || [],
    [suggestedSearches]
  );

  const suggestedQueries = React.useMemo(
    () =>
      suggestedSearchHits
        .map((hit) => hit.name)
        .filter((name): name is string => !!name)
        .slice(0, 6), // Limit to max 6 suggestions
    [suggestedSearchHits]
  );

  const maxChipsPerRow = React.useMemo(() => {
    if (measuredMaxChipsPerRow !== null) {
      return Math.max(3, measuredMaxChipsPerRow);
    }

    return 3;
  }, [measuredMaxChipsPerRow]);

  const firstRowEndIndex = React.useMemo(() => {
    if (suggestedQueries.length <= maxChipsPerRow) {
      return suggestedQueries.length;
    }

    const firstRowCount = Math.min(maxChipsPerRow, suggestedQueries.length);
    const remainingCount = suggestedQueries.length - firstRowCount;

    if (Math.abs(firstRowCount - remainingCount) <= 1) {
      return Math.ceil(suggestedQueries.length / 2);
    }

    return firstRowCount;
  }, [suggestedQueries.length, maxChipsPerRow]);

  useEffect(() => {
    if (!chipContainerRef.current || suggestedQueries.length === 0) {
      return;
    }

    const measureChips = () => {
      const container = chipContainerRef.current;
      if (!container) return;

      const containerWidth = container.getBoundingClientRect().width;
      const allChipButtons = container.querySelectorAll('button');
      if (allChipButtons.length === 0) return;

      let totalWidth = 0;
      let chipsThatFit = 0;
      const gap = 8; // var(--cds-spacing-100) = 8px

      for (let i = 0; i < allChipButtons.length; i += 1) {
        const chip = allChipButtons[i] as HTMLElement;
        const chipWidth = chip.getBoundingClientRect().width;

        const widthWithGap = totalWidth + (i > 0 ? gap : 0) + chipWidth;

        if (widthWithGap <= containerWidth) {
          totalWidth = widthWithGap;
          chipsThatFit = i + 1;
        } else {
          break;
        }
      }

      const finalChipsThatFit = Math.max(3, chipsThatFit);
      if (finalChipsThatFit !== measuredMaxChipsPerRow) {
        setMeasuredMaxChipsPerRow(finalChipsThatFit);
      }
    };

    const timeoutId = setTimeout(measureChips, 0);

    const resizeObserver = new ResizeObserver(() => {
      measureChips();
    });

    if (chipContainerRef.current) {
      resizeObserver.observe(chipContainerRef.current);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedQueries, firstRowEndIndex]);

  // Reset measured chip count when queries change
  useEffect(() => {
    setMeasuredMaxChipsPerRow(null);
  }, [suggestedQueries]);

  if (suggestedQueries.length === 0) {
    return null;
  }

  const handleQueryClick = (query: string) => {
    onQueryClick?.(query);
  };

  return (
    <>
      <div data-testid="nl-suggestions">
        <div css={styles.contentWrapper}>
          <Typography2 variant="bodyPrimary" component="span" css={styles.suggestedQueriesLabel}>
            {_t('More to explore:')}
          </Typography2>
          <div ref={chipContainerRef} css={styles.chipContainer}>
            {/* First row */}
            <div css={styles.chipRow} data-chip-row>
              {suggestedQueries.slice(0, firstRowEndIndex).map((query) => (
                <ChipButton
                  key={query}
                  onClick={() => handleQueryClick(query)}
                  height={AI_SUMMARY_CHIP_BUTTON_HEIGHT}
                  isNlSuggestion={true}
                  trackingName="search_page_suggested_search"
                  trackingData={{
                    href: `${ON_SITE_SEARCH_PATH}?query=${query.toLowerCase().trim()}`,
                    suggestionSource: 'ai_summary',
                  }}
                >
                  {query}
                </ChipButton>
              ))}
            </div>
            {/* Second row - only leftover chips */}
            {firstRowEndIndex < suggestedQueries.length && (
              <div css={styles.chipRow}>
                {suggestedQueries.slice(firstRowEndIndex).map((query) => (
                  <ChipButton
                    key={query}
                    onClick={() => handleQueryClick(query)}
                    height={AI_SUMMARY_CHIP_BUTTON_HEIGHT}
                    isNlSuggestion={true}
                    trackingName="search_page_suggested_search"
                    trackingData={{
                      href: `${ON_SITE_SEARCH_PATH}?query=${query.toLowerCase().trim()}`,
                      suggestionSource: 'ai_summary',
                    }}
                  >
                    {query}
                  </ChipButton>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div css={styles.dividerWrapper}>
        <Divider />
      </div>
    </>
  );
};

export default NLSuggestions;
